import { useState } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { logger } from "@/lib/logger";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { Paperclip, Lock } from "lucide-react";
import { AnexoRow } from "./AnexoRow";

// Deve espelhar ALLOWED_DOC_TYPES do backend
export const TIPO_DOCS = [
  { key: "cpf", label: "CPF" },
  { key: "rg", label: "RG" },
  { key: "cnh", label: "CNH" },
  { key: "comprovante_residencia", label: "Comprovante de Residência" },
  { key: "cadunico", label: "CadÚnico" },
  { key: "certidao_casamento", label: "Certidão de Casamento" },
  { key: "certidao_nascimento", label: "Certidão de Nascimento" },
  { key: "laudo_pcd", label: "Laudo PCD" },
  { key: "outros", label: "Outros" },
];

const MAX_MB = 20;
const BYTES_PER_MB = 1024 * 1024; // 1 MiB

export default function AnexosSection({ inscricao, onChanged }) {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(null); // tipo em upload
  const [deleting, setDeleting] = useState(null); // doc_id em delete

  const canManage = user?.role === "super_admin" ||
    (user?.role === "funcionario" && (user?.permissoes || []).includes("inscricoes.anexos"));

  const docs = inscricao?.documentos || [];
  const byTipo = TIPO_DOCS.reduce((acc, t) => {
    acc[t.key] = docs.filter((d) => d.tipo === t.key);
    return acc;
  }, {});

  const doUpload = async (tipo, file) => {
    if (!file) return;
    if (file.size > MAX_MB * BYTES_PER_MB) {
      toast.error(`Arquivo maior que ${MAX_MB}MB`);
      return;
    }
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["pdf", "jpg", "jpeg", "png"].includes(ext)) {
      toast.error("Apenas PDF, JPG e PNG são permitidos");
      return;
    }
    setUploading(tipo);
    try {
      const fd = new FormData();
      fd.append("tipo", tipo);
      fd.append("file", file);
      const r = await api.post(`/inscricoes/${inscricao.id}/documentos`, fd);
      if (r.data?.substituiu) {
        toast.success("Documento substituído com sucesso");
      } else {
        toast.success("Documento enviado com sucesso");
      }
      onChanged?.();
    } catch (e) {
      logger.error("Falha upload", e);
      toast.error(e.response?.data?.detail || "Falha no upload");
    } finally {
      setUploading(null);
    }
  };

  const doDelete = async (doc) => {
    if (!window.confirm(`Excluir o documento "${doc.filename}"?`)) return;
    setDeleting(doc.id);
    try {
      await api.delete(`/inscricoes/${inscricao.id}/documentos/${doc.id}`);
      toast.success("Documento excluído");
      onChanged?.();
    } catch (e) {
      logger.error("Falha delete", e);
      toast.error(e.response?.data?.detail || "Falha ao excluir");
    } finally {
      setDeleting(null);
    }
  };

  const openDownload = (doc) => {
    window.open(`${API}/inscricoes/${inscricao.id}/documentos/${doc.id}/download`, "_blank");
  };

  if (!canManage) {
    return (
      <Card data-testid="anexos-section-readonly">
        <CardHeader>
          <CardTitle className="text-base text-[#083CA6] flex items-center gap-2">
            <Paperclip className="w-4 h-4" /> Documentos Anexados
          </CardTitle>
          <CardDescription>Sem permissão para gerenciar anexos.</CardDescription>
        </CardHeader>
        <CardContent>
          <Alert>
            <Lock className="w-4 h-4" />
            <AlertDescription className="text-sm">
              Você não tem permissão <code>inscricoes.anexos</code>. Peça ao Super Administrador para habilitar.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="anexos-section">
      <CardHeader>
        <CardTitle className="text-base text-[#083CA6] flex items-center gap-2">
          <Paperclip className="w-4 h-4" /> Documentos Anexados
        </CardTitle>
        <CardDescription>
          Envie ou substitua os documentos da inscrição. Substituir remove o anterior automaticamente. Tamanho máximo: {MAX_MB}MB · PDF, JPG, PNG.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {TIPO_DOCS.map((tipo) => (
          <AnexoRow
            key={tipo.key}
            tipo={tipo}
            enviados={byTipo[tipo.key] || []}
            isUploading={uploading === tipo.key}
            deleting={deleting}
            onUpload={doUpload}
            onDownload={openDownload}
            onDelete={doDelete}
          />
        ))}
      </CardContent>
    </Card>
  );
}
